import { Platform } from 'react-native';
import { MARKET, TMarket } from '@/common/constants/markets';
import { ROUTES, replaceRouteParams } from '@/common/constants/routes';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import { TFeatureFlag } from '@/common/integrations/feature-flag/feature-flag.types';
import { useGlobalSearchParams } from 'expo-router';
import { useMarket } from './useMarket';

/**
 * TabsWrapper component is applying the page layout styles automatically for every web route
 * (this was done to automatize the universal sidebar implementation)
 * but we need to exclude some routes from this behavior because now each route will have its own layout.
 *
 * We need to keep updating this list as we migrate more pages. And clean them as we remove the feature flags.
 */
export const migratedPages: {
  path: string;
  featureFlag?: TFeatureFlag;
  market?: TMarket[];
}[] = [
  // Discounts
  {
    path: ROUTES.DISCOUNTS,
  },

  // Card
  {
    path: ROUTES.CARD,
    featureFlag: 'ff_sea_account_card_management_home',
  },

  // Account
  {
    path: ROUTES.CARD_NEW_REQUEST,
    featureFlag: 'ff_sea_account_card_management_request',
  },
  {
    path: ROUTES.COMPANY_DETAILS,
  },
  {
    path: ROUTES.PROFILE,
  },
  {
    path: ROUTES.PERSONAL_DETAILS,
  },
  {
    path: ROUTES.DOCUMENT_DETAILS,
  },
  {
    path: ROUTES.DOCUMENT_FORM,
  },
  {
    path: ROUTES.FAMILY_MEMBERS,
  },

  // Insurance
  {
    path: ROUTES.INSURANCE,
  },
  {
    path: ROUTES.ADD_INSURANCE_MEMBERS,
    featureFlag: 'ff_family_inclusion_simulation',
  },

  // IT Meal
  {
    path: ROUTES.MEAL_VOUCHER_PAYMENT,
    featureFlag: 'ff_single_meal_transaction',
  },
  {
    path: ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC,
    featureFlag: 'ff_single_meal_transaction',
  },
  {
    path: ROUTES.MEAL_VOUCHER_PAYMENT_ADD_CARD,
    featureFlag: 'ff_single_meal_transaction',
  },

  // Home page
  {
    path: ROUTES.ROOT,
    market: [MARKET.IT],
    featureFlag: 'ff_single_meal_transaction',
  },
  {
    path: ROUTES.ROOT,
    market: [MARKET.ES],
  },
  {
    path: ROUTES.ROOT,
    market: [MARKET.PT],
  },

  // Budgets
  {
    path: ROUTES.BUDGETS,
    featureFlag: 'ff_sea_budgets_home',
  },
  {
    path: ROUTES.BUDGETS_DETAIL,
    featureFlag: 'ff_sea_budget_detail',
  },
  {
    path: ROUTES.BUDGETS_ACTIVITY,
    featureFlag: 'ff_sea_budgets_home',
  },
  // Activity
  {
    path: ROUTES.ACTIVITY,
    featureFlag: 'ff_sea_activity',
  },
  {
    path: ROUTES.HOME_ACTIVITY,
    featureFlag: 'ff_sea_activity',
  },

  // TODO: Remove this once flow layout is battle tested
  {
    path: '/profile/flow-test',
  },
  {
    path: '/playground',
  },
];

export function usePagesMigrations(pathname: string) {
  const searchParams = useGlobalSearchParams();
  const { features } = useFeatures();
  const { slug } = useMarket();

  // We only need this to check the array in web.
  if (Platform.OS !== 'web') {
    return {
      isPageMigrated: false,
    };
  }

  const currentPage = migratedPages.find((page) => {
    const params = Object.values(searchParams) as string[];
    const route = replaceRouteParams(page.path, ...params);
    const checkMarket = page.market ? page.market.includes(slug) : true;
    return route === pathname && checkMarket;
  });

  if (!currentPage) {
    return {
      isPageMigrated: false,
    };
  }

  if (currentPage.featureFlag && !features[currentPage.featureFlag]) {
    return {
      isPageMigrated: false,
    };
  }

  return {
    isPageMigrated: true,
  };
}
